/**
 * @file components/dispatch/CaseEditDrawer.tsx
 * @description 修改案件與申請明細抽屜。
 *
 * 對應 Excel 行 42 / 45：
 * - 可修改驗證服務類型、驗證類別、申請品項/範圍/面積與兩類場所；
 * - 「驗證資格」只在客戶檔案維護，本抽屜不再提供；
 * - TAP 品項連結作物管理，保存 cropId 與當下文字快照（序16 起非必填）。
 *
 * 2026-09-13 回報序14：申請明細改與「新建案件」同一格式——共用 ApplicationDetailsFields
 * （有機：驗證品項下拉＋驗證範圍自由文字＋依驗證服務類型分展示面積；產銷履歷：多行明細＋附件），
 * 表單值 ⇄ applicationDetails 轉換共用 application-details-form.ts；有機儲存前同樣比對
 * 作物管理並提示補建驗證範圍（序7）。
 *
 * 權限由前後端 `case.dispatch` 雙重把關。送出時保留申請明細中未在本畫面
 * 編輯的通用欄位（驗證選項、銷售通路等），避免局部修改造成資料遺失。
 */

import { Button, Checkbox, DatePicker, Drawer, Form, Input, Select, Space } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useMemo, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  VERIFICATION_TYPES,
  applicationKindForScheme,
  isTrackingVerificationType,
  joinVerificationTypes,
  parseApplicationDetails,
  splitVerificationTypes,
  sumTapArea,
  type ApplicationDetails,
  type CaseApplicationDetails,
} from '@zhcb/shared';
import { apiClient } from '@/lib/api-client';
import { message } from '@/lib/antd-app';
import {
  useContractSources,
  useVerificationCategories,
  useVerificationTypes,
} from '@/hooks/useClientLookups';
import { useCropOptions } from '@/hooks/useCrops';
import {
  ApplicationDetailsFields,
  confirmAndEnsureTopScopes,
} from '@/components/dispatch/ApplicationDetailsFields';
import {
  buildTapRows,
  buildTopAreaFields,
  buildTopProductEntries,
  parseTopProductGroups,
  type ApplicationDetailsFormValues,
} from '@/components/dispatch/application-details-form';

/** 客戶土地（案件場所選擇只需這三欄）。 */
interface ClientLandItem {
  id: number;
  parcelNo: string | null;
  address: string;
}

/** 客戶採收後處理場所（案件場所選擇只需這三欄）。 */
interface ClientSiteItem {
  id: number;
  name: string | null;
  address: string;
}

/** 抽屜需要的案件當前值。 */
export interface CaseEditInitial {
  id: number;
  clientId: number;
  scheme: string;
  verificationType: string;
  verificationCategories: unknown;
  contractSource: string | null;
  applicationDetails: unknown;
  dueDate: string | null;
  notes: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  initial: CaseEditInitial;
  onSaved: () => void;
}

/** 修改案件表單值（申請明細欄位見 ApplicationDetailsFormValues，與新建案件同名同格式）。 */
interface EditFormValues extends ApplicationDetailsFormValues {
  verificationType: string[];
  verificationCategories?: string[];
  contractSource?: string;
  dueDate?: dayjs.Dayjs | null;
  notes?: string;
  selectedLandIds?: number[];
  selectedSiteIds?: number[];
}

/** 土地快照文字：地址加地號，確保歷史可追溯。 */
function landSnapshotText(land: ClientLandItem): string {
  return land.parcelNo?.trim() ? `${land.address}（地號 ${land.parcelNo.trim()}）` : land.address;
}

/** 採後場所快照文字：場所名稱加地址。 */
function siteSnapshotText(site: ClientSiteItem): string {
  return site.name?.trim() ? `${site.name.trim()}：${site.address}` : site.address;
}

/**
 * 只取 TOP/TAP 共用欄位，避免把舊 kind 專屬欄位錯帶到另一種結構。
 * 這些欄位雖不在本抽屜編輯，仍需原樣保留。
 */
function commonApplicationFields(details: ApplicationDetails | undefined): ApplicationDetails {
  if (!details) return {};
  return {
    verificationOption: details.verificationOption,
    groupFarmerCount: details.groupFarmerCount,
    counselorName: details.counselorName,
    counselorPhone: details.counselorPhone,
    salesChannels: details.salesChannels,
    salesAuctionName: details.salesAuctionName,
    salesSupermarketName: details.salesSupermarketName,
    salesProcessorName: details.salesProcessorName,
    salesDistributorName: details.salesDistributorName,
    salesOtherName: details.salesOtherName,
  };
}

/**
 * 依選中的 master id 重新組地址快照。
 * 若 master 已停用/刪除，回落原快照，避免修改其他欄位時把歷史地址清掉。
 */
function selectedSnapshots<T extends { id: number }>(
  selectedIds: number[],
  masters: T[],
  textOf: (item: T) => string,
  priorIds: number[] | undefined,
  priorSnapshots: string[] | undefined,
): string[] {
  return selectedIds
    .map((id) => {
      const current = masters.find((item) => item.id === id);
      if (current) return textOf(current);
      const priorIndex = priorIds?.indexOf(id) ?? -1;
      return priorIndex >= 0 ? priorSnapshots?.[priorIndex] : undefined;
    })
    .filter((value): value is string => !!value);
}

/** 修改案件與申請明細抽屜。 */
export function CaseEditDrawer({ open, onClose, initial, onSaved }: Props) {
  const [form] = Form.useForm<EditFormValues>();
  const queryClient = useQueryClient();
  const detailKind = applicationKindForScheme(initial.scheme);
  const parsedDetails = useMemo(
    () => parseApplicationDetails(initial.applicationDetails),
    [initial.applicationDetails],
  );

  const sourcesQuery = useContractSources({ activeOnly: true });
  const verificationTypesQuery = useVerificationTypes({ activeOnly: true });
  const categoriesQuery = useVerificationCategories({ activeOnly: true });
  // 作物字典按方案過濾（與新建案件一致：TOP 取有機一覽表、TAP 取產銷履歷字典）
  const cropsQuery = useCropOptions({ scheme: detailKind, enabled: open });
  const landsQuery = useQuery<{ items: ClientLandItem[] }>({
    queryKey: ['client-lands-for-case-edit', initial.clientId],
    queryFn: () => apiClient.get(`/api/clients/${initial.clientId}/lands`),
    enabled: open && initial.clientId > 0,
  });
  const sitesQuery = useQuery<{ items: ClientSiteItem[] }>({
    queryKey: ['client-sites-for-case-edit', initial.clientId],
    queryFn: () => apiClient.get(`/api/clients/${initial.clientId}/processing-sites`),
    enabled: open && initial.clientId > 0,
  });

  const clientLands = landsQuery.data?.items ?? [];
  const clientSites = sitesQuery.data?.items ?? [];
  const cropItems = useMemo(() => cropsQuery.data?.items ?? [], [cropsQuery.data]);
  const verificationTypeItems = verificationTypesQuery.data?.items ?? [];
  const verificationTypeOptions =
    verificationTypeItems.length > 0
      ? verificationTypeItems.map((item) => ({ value: item.name, label: item.name }))
      : VERIFICATION_TYPES.map((type) => ({ value: type, label: type }));
  const filteredCategories = (categoriesQuery.data?.items ?? []).filter(
    (category) => !category.scheme || category.scheme === initial.scheme,
  );
  const verificationType = Form.useWatch('verificationType', form);
  const isTrackingArea = isTrackingVerificationType(verificationType);

  /**
   * 本次開啟是否已完成預填：有機品項需對照作物字典轉成下拉鍵，須等字典載入後才預填；
   * 預填只做一次，避免字典背景刷新時覆蓋使用者正在編輯的內容。關閉抽屜時重置。
   */
  const initializedRef = useRef(false);
  useEffect(() => {
    if (!open) initializedRef.current = false;
  }, [open]);

  // 每次開啟都從後端最新案件值重設，避免上次未送出的草稿殘留。
  useEffect(() => {
    if (!open || initializedRef.current) return;
    // 有機案件等作物字典載入後再預填（品項名稱 → 下拉鍵）
    if (detailKind === 'TOP' && cropsQuery.isLoading) return;
    initializedRef.current = true;
    const categories = Array.isArray(initial.verificationCategories)
      ? initial.verificationCategories.filter((value): value is string => typeof value === 'string')
      : [];
    const base: EditFormValues = {
      verificationType: splitVerificationTypes(initial.verificationType),
      verificationCategories: categories,
      contractSource: initial.contractSource ?? undefined,
      dueDate: initial.dueDate ? dayjs(initial.dueDate) : null,
      notes: initial.notes ?? undefined,
    };

    if (parsedDetails?.kind === 'TOP') {
      const details = parsedDetails.details;
      const tracking = isTrackingVerificationType(base.verificationType);
      // 存量案件只有 verificationArea 總值時，依驗證服務類型回填到對應面積欄
      const legacyArea = details.verificationArea ? Number(details.verificationArea) : undefined;
      const hasSplitArea =
        details.applyAreaHa !== undefined ||
        details.organicAreaHa !== undefined ||
        details.organicConversionAreaHa !== undefined;
      Object.assign(base, {
        topProductGroups: parseTopProductGroups(
          details.productItems,
          details.productScopes,
          cropItems,
        ),
        topApplyAreaHa:
          details.applyAreaHa ?? (!hasSplitArea && !tracking ? legacyArea : undefined),
        topOrganicAreaHa:
          details.organicAreaHa ?? (!hasSplitArea && tracking ? legacyArea : undefined),
        topOrganicConversionAreaHa: details.organicConversionAreaHa,
        selectedLandIds: details.selectedLandIds ?? [],
        selectedSiteIds: details.selectedSiteIds ?? [],
      });
    } else if (parsedDetails?.kind === 'TAP') {
      const details = parsedDetails.details;
      Object.assign(base, {
        tapRows: details.rows,
        tapLandCount: details.landCount,
        tapSiteCount: details.siteCount,
        selectedLandIds: details.selectedLandIds ?? [],
        selectedSiteIds: details.selectedSiteIds ?? [],
      });
    } else if (detailKind === 'TAP') {
      base.tapRows = [{}];
    }

    form.setFieldsValue(base);
  }, [cropItems, cropsQuery.isLoading, detailKind, form, initial, open, parsedDetails]);

  const updateMutation = useMutation({
    mutationFn: (values: EditFormValues) => {
      const selectedLandIds = values.selectedLandIds ?? [];
      const selectedSiteIds = values.selectedSiteIds ?? [];
      const prior =
        parsedDetails?.kind === 'TOP' || parsedDetails?.kind === 'TAP'
          ? parsedDetails.details
          : undefined;
      const siteSelection = {
        selectedLandIds: selectedLandIds.length ? selectedLandIds : undefined,
        selectedSiteIds: selectedSiteIds.length ? selectedSiteIds : undefined,
        landAddressSnapshot: selectedLandIds.length
          ? selectedSnapshots(
              selectedLandIds,
              clientLands,
              landSnapshotText,
              prior?.selectedLandIds,
              prior?.landAddressSnapshot,
            )
          : undefined,
        siteAddressSnapshot: selectedSiteIds.length
          ? selectedSnapshots(
              selectedSiteIds,
              clientSites,
              siteSnapshotText,
              prior?.selectedSiteIds,
              prior?.siteAddressSnapshot,
            )
          : undefined,
      };
      const common = commonApplicationFields(parsedDetails?.details);
      let applicationDetails: CaseApplicationDetails;

      if (detailKind === 'TAP') {
        // 序16：非必填，未選品項的空行略過
        const rows = buildTapRows(values.tapRows);
        applicationDetails = {
          ...common,
          kind: 'TAP',
          rows,
          landCount: values.tapLandCount,
          siteCount: values.tapSiteCount,
          totalArea: sumTapArea(rows),
          ...siteSelection,
        };
      } else {
        // 序6/序14：與新建案件相同的 驗證品項＋驗證範圍（自由文字）＋分項面積 組裝
        const { productItems, productScopes } = buildTopProductEntries(values.topProductGroups);
        applicationDetails = {
          ...common,
          kind: 'TOP',
          productItems: productItems.length ? productItems : undefined,
          productScopes: productScopes.length ? productScopes : undefined,
          ...buildTopAreaFields(values, values.verificationType),
          ...siteSelection,
        };
      }

      return apiClient.patch(`/api/cases/${initial.id}`, {
        verificationType: joinVerificationTypes(values.verificationType),
        verificationCategories: values.verificationCategories ?? [],
        contractSource: values.contractSource?.trim() || undefined,
        applicationDetails,
        // null 代表明確清除；undefined 才是不修改。
        dueDate: values.dueDate ? values.dueDate.toISOString() : null,
        notes: values.notes?.trim() || undefined,
      });
    },
    onSuccess: () => {
      message.success('案件與申請明細已更新');
      onSaved();
      onClose();
    },
    onError: (error: Error) => {
      console.error('[CaseEditDrawer] update case failed', error);
      message.error(`更新失敗：${error.message ?? '請稍後再試'}`);
    },
  });

  return (
    <Drawer
      title="修改案件"
      open={open}
      onClose={onClose}
      size="large"
      destroyOnClose
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Space>
            <Button onClick={onClose}>取消</Button>
            <Button
              type="primary"
              loading={updateMutation.isPending}
              onClick={() => void form.submit()}
            >
              儲存
            </Button>
          </Space>
        </div>
      }
    >
      <Form<EditFormValues>
        form={form}
        layout="vertical"
        requiredMark
        onFinish={(values) => {
          void (async () => {
            // 序7：有機案件儲存前比對作物管理，確認是否補建尚未建立的驗證範圍（不阻斷儲存）
            if (detailKind === 'TOP') {
              await confirmAndEnsureTopScopes(values.topProductGroups, cropItems, () => {
                void queryClient.invalidateQueries({ queryKey: ['settings', 'crops'] });
              });
            }
            updateMutation.mutate(values);
          })();
        }}
      >
        <Form.Item
          name="verificationType"
          label="驗證服務類型"
          rules={[{ required: true, type: 'array', min: 1, message: '請選擇驗證服務類型' }]}
        >
          <Select
            mode="multiple"
            showSearch
            allowClear
            loading={verificationTypesQuery.isLoading}
            options={verificationTypeOptions}
            optionFilterProp="label"
          />
        </Form.Item>

        <Form.Item name="verificationCategories" label="驗證類別">
          <Select
            mode="multiple"
            showSearch
            allowClear
            loading={categoriesQuery.isLoading}
            options={filteredCategories.map((item) => ({ value: item.name, label: item.name }))}
            optionFilterProp="label"
          />
        </Form.Item>

        <Form.Item name="contractSource" label="合約來源">
          <Select
            showSearch
            allowClear
            loading={sourcesQuery.isLoading}
            options={(sourcesQuery.data?.items ?? []).map((item) => ({
              value: item.name,
              label: item.name,
            }))}
            optionFilterProp="label"
          />
        </Form.Item>

        {/* 序14：申請明細與新建案件共用同一欄位區塊 */}
        <Form.Item
          label={detailKind === 'TAP' ? '申請明細（產銷履歷，選填）' : '申請明細（有機）'}
          style={{ marginBottom: 0 }}
        >
          <ApplicationDetailsFields
            form={form}
            detailKind={detailKind}
            cropItems={cropItems}
            cropsLoading={cropsQuery.isLoading}
            isTrackingArea={isTrackingArea}
          />
        </Form.Item>

        <div style={{ marginTop: 24 }}>
          <Form.Item label="驗證場所地址（客戶土地）" name="selectedLandIds">
            <Checkbox.Group
              style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
              options={clientLands.map((land) => ({
                value: land.id,
                label: landSnapshotText(land),
              }))}
            />
          </Form.Item>
          <Form.Item label="採收後處理／加工場所地址" name="selectedSiteIds">
            <Checkbox.Group
              style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
              options={clientSites.map((site) => ({
                value: site.id,
                label: siteSnapshotText(site),
              }))}
            />
          </Form.Item>
        </div>

        <Form.Item name="dueDate" label="到期日" tooltip="可清除；作為 SLA 逾期預警基準">
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="notes" label="備注" style={{ marginBottom: 0 }}>
          <Input.TextArea rows={3} maxLength={2000} showCount />
        </Form.Item>
      </Form>
    </Drawer>
  );
}
